import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Divider,
  Paper,
  Fade,
  Zoom,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  ShoppingCart,
  AttachMoney,
  PersonAdd,
  Notifications,
  MoreVert,
  Circle,
  Refresh,
  Download,
} from '@mui/icons-material';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { apiClient as api } from '../services/api';
import { useAppSelector } from '../hooks/redux';
import '../styles/minimal-padding.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// Metric Card Component
interface MetricCardProps {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  bgGradient: string;
  icon: React.ReactNode;
}

// Metric Card Component with forwardRef
interface MetricCardProps {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  bgGradient: string;
  icon: React.ReactNode;
}

const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(({
  title,
  value,
  change,
  trend,
  bgGradient,
  icon,
}, ref) => (
  <Card
    ref={ref}
    className="metric-card"
    sx={{
      background: bgGradient,
      position: 'relative',
      overflow: 'hidden',
      minHeight: 140,
      borderRadius: 4,
      border: 'none',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
      color: '#FFFFFF',
      cursor: 'pointer',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-8px) scale(1.02)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
        transform: 'translateX(-100%)',
        transition: 'transform 0.6s',
      },
      '&:hover::before': {
        transform: 'translateX(100%)',
      },
    }}
  >
    <CardContent sx={{ position: 'relative', zIndex: 2, p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#FFFFFF !important',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '1.5px',
              mb: 1,
              display: 'block',
              opacity: 0.9,
            }}
            style={{ color: '#FFFFFF !important' }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h3" 
            sx={{ 
              color: '#FFFFFF !important',
              fontWeight: 800,
              fontSize: { xs: '1.8rem', sm: '2.2rem' },
              lineHeight: 1.2,
              mb: 1,
            }}
            style={{ color: '#FFFFFF !important' }}
          >
            {value}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {trend === 'up' ? (
              <TrendingUp sx={{ fontSize: 18, color: '#FFFFFF' }} />
            ) : (
              <TrendingDown sx={{ fontSize: 18, color: '#FFFFFF' }} />
            )}
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#FFFFFF !important',
                fontSize: '0.85rem',
                fontWeight: 600,
                opacity: 0.9,
              }}
              style={{ color: '#FFFFFF !important' }}
            >
              {change}
            </Typography>
          </Box>
        </Box>
        <Avatar 
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.2)', 
            width: 70, 
            height: 70,
            color: 'white',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Box sx={{ fontSize: { xs: 30, sm: 35 } }}>
            {icon}
          </Box>
        </Avatar>
      </Box>
    </CardContent>
  </Card>
));

MetricCard.displayName = 'MetricCard';

// Activity Item Component
interface ActivityItemProps {
  type: 'order' | 'customer' | 'alert' | 'update';
  title: string;
  description: string;
  time: string;
  avatar?: string;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ type, title, description, time, avatar }) => {
  const getAvatarColor = () => {
    switch (type) {
      case 'order': return '#4299E1';
      case 'customer': return '#48BB78';
      case 'alert': return '#ED8936';
      case 'update': return '#9F7AEA';
      default: return '#E8B4B8';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'order': return <ShoppingCart sx={{ fontSize: 16 }} />;
      case 'customer': return <PersonAdd sx={{ fontSize: 16 }} />;
      case 'alert': return <Notifications sx={{ fontSize: 16 }} />;
      case 'update': return <TrendingUp sx={{ fontSize: 16 }} />;
      default: return <Circle sx={{ fontSize: 16 }} />;
    }
  };

  return (
    <ListItem 
      sx={{ 
        px: 0, 
        py: 1.5,
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: 'rgba(232, 180, 184, 0.08)',
          transform: 'translateX(4px)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <ListItemAvatar>
        <Avatar
          sx={{
            bgcolor: getAvatarColor(),
            width: 40,
            height: 40,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.1)',
            },
          }}
          src={avatar}
        >
          {!avatar && getIcon()}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography 
            variant="subtitle2" 
            component="div"
            sx={{ 
              fontWeight: 600, 
              color: 'text.primary',
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
        }
        secondary={
          <>
            <Typography 
              variant="body2" 
              component="span"
              color="text.secondary" 
              sx={{ 
                display: 'block',
                mb: 0.5,
                lineHeight: 1.4,
              }}
            >
              {description}
            </Typography>
            <Chip
              label={time}
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                borderColor: getAvatarColor(),
                color: getAvatarColor(),
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
          </>
        }
        secondaryTypographyProps={{
          component: 'div'
        }}
      />
    </ListItem>
  );
};

// Product Item Component
interface ProductItemProps {
  name: string;
  sales: number;
  revenue: string;
  status: 'trending' | 'stable' | 'declining';
  color: string;
}

const ProductItem: React.FC<ProductItemProps> = ({ name, sales, revenue, status, color }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'space-between', 
      py: 1.5,
      px: 2,
      borderRadius: 2,
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: 'rgba(232, 180, 184, 0.08)',
        transform: 'translateX(4px)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          bgcolor: color,
          boxShadow: `0 0 0 3px ${color}20`,
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.2)',
          },
        }}
      />
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 600,
          color: 'text.primary',
          minWidth: 0,
          flex: 1,
        }}
      >
        {name}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 200 }}>
      <Box sx={{ textAlign: 'center', minWidth: 40 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {sales}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          units
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right', minWidth: 80 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          {revenue}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 80 }}>
        <Chip
          label={status}
          size="small"
          color={status === 'trending' ? 'success' : status === 'stable' ? 'primary' : 'warning'}
          sx={{ 
            minWidth: 70, 
            fontSize: '0.7rem',
            fontWeight: 600,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
            '&:hover': {
              transform: 'scale(1.05)',
            },
            transition: 'transform 0.2s ease-in-out',
          }}
        />
      </Box>
    </Box>
  </Box>
);

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [refreshing, setRefreshing] = useState(false);
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics/comprehensive-dashboard');
      setDashboardData(response.data.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  if (loading && !dashboardData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  const stats = dashboardData || {};
  
  // Chart data
  const salesData = {
    labels: stats.monthlyRevenue?.map((m: any) => m.month) || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Revenue',
        data: stats.monthlyRevenue?.map((m: any) => m.revenue) || [30000, 35000, 32000, 40000, 45000, 50000, 48000, 52000, 55000, 58000, 60000, 65000],
        borderColor: '#4299E1',
        backgroundColor: 'rgba(66, 153, 225, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const trafficData = {
    labels: stats.topServices?.map((s: any) => s.name).slice(0, 3) || ['Direct', 'Social', 'Referral'],
    datasets: [
      {
        data: stats.topServices?.map((s: any) => s.bookings).slice(0, 3) || [45, 30, 25],
        backgroundColor: ['#4299E1', '#48BB78', '#ED8936'],
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          color: 'rgba(0,0,0,0.05)',
        },
        border: {
          display: false,
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    cutout: '70%',
  };

  return (
    <Box sx={{ width: '100%', height: '100%', p: 2 }}>
      {/* Enhanced Header Section with Animation */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 3, 
          background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(232, 180, 184, 0.3)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 800,
                  color: '#5C2A30',
                  mb: 1,
                }}
              >
                Dashboard Overview
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(92, 42, 48, 0.85)',
                  fontSize: '1.2rem',
                  fontWeight: 500,
                }}
              >
                Welcome back! Here's what's happening at your salon today
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.4)',
                  color: '#5C2A30',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(92, 42, 48, 0.18)',
                  width: 48,
                  height: 48,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.6)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {refreshing ? <CircularProgress size={24} color="inherit" /> : <Refresh />}
              </IconButton>
              <IconButton
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.4)',
                  color: '#5C2A30',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(92, 42, 48, 0.18)',
                  width: 48,
                  height: 48,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.6)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <Download />
              </IconButton>
            </Box>
          </Box>
        </Box>
        
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            left: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
      </Paper>

      {/* Enhanced Metric Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={300} style={{ transitionDelay: '100ms' }}>
            <MetricCard
              title="Revenue"
              value={`₹${stats.revenue?.current?.toLocaleString() || '0'}`}
              change={`${stats.revenue?.change > 0 ? '↑' : '↓'} ${Math.abs(stats.revenue?.change || 0).toFixed(1)}%`}
              trend={stats.revenue?.change >= 0 ? 'up' : 'down'}
              bgGradient="linear-gradient(135deg, #10B981 0%, #059669 100%)"
              icon={<AttachMoney />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={400} style={{ transitionDelay: '200ms' }}>
            <MetricCard
              title="Appointments"
              value={`${stats.appointments?.current || 0}`}
              change={`${stats.appointments?.change > 0 ? '↑' : '↓'} ${Math.abs(stats.appointments?.change || 0).toFixed(1)}%`}
              trend={stats.appointments?.change >= 0 ? 'up' : 'down'}
              bgGradient="linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)"
              icon={<ShoppingCart />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={500} style={{ transitionDelay: '300ms' }}>
            <MetricCard
              title="Customers"
              value={`${stats.customers?.total || 0}`}
              change={`${stats.customers?.newThisMonth || 0} new`}
              trend="up"
              bgGradient="linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)"
              icon={<People />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={600} style={{ transitionDelay: '400ms' }}>
            <MetricCard
              title="Avg Booking Value"
              value={`₹${stats.averageBookingValue?.toFixed(0) || '0'}`}
              change={`${stats.revenue?.change > 0 ? '↑' : '↓'} ${Math.abs(stats.revenue?.change || 0).toFixed(1)}%`}
              trend={stats.revenue?.change >= 0 ? 'up' : 'down'}
              bgGradient="linear-gradient(135deg, #F97316 0%, #EA580C 100%)"
              icon={<TrendingUp />}
            />
          </Zoom>
        </Grid>
      </Grid>

      {/* Enhanced Charts and Analytics */}
      <Grid container spacing={3}>
        {/* Sales Overview Chart */}
        <Grid item xs={12} lg={8}>
          <Fade in timeout={700} style={{ transitionDelay: '500ms' }}>
            <Card 
              sx={{ 
                height: 420,
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(232, 180, 184, 0.2)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #4299E1 0%, #48BB78 100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #4299E1 0%, #48BB78 100%)',
                        boxShadow: '0 4px 12px rgba(66, 153, 225, 0.3)',
                      }}
                    >
                      <TrendingUp sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        Sales Overview
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#2D3748', fontWeight: 500 }}>
                        Track your revenue performance over time
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton 
                    size="small"
                    sx={{
                      bgcolor: 'rgba(232, 180, 184, 0.1)',
                      color: '#E8B4B8',
                      '&:hover': {
                        bgcolor: '#E8B4B8',
                        color: 'white',
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <MoreVert />
                  </IconButton>
                </Box>
                <Box sx={{ height: 300 }}>
                  <Line data={salesData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Traffic Sources */}
        <Grid item xs={12} lg={4}>
          <Fade in timeout={800} style={{ transitionDelay: '600ms' }}>
            <Card 
              sx={{ 
                height: 420,
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(232, 180, 184, 0.2)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #ED8936 0%, #4299E1 50%, #48BB78 100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #ED8936 0%, #4299E1 100%)',
                      boxShadow: '0 4px 12px rgba(237, 137, 54, 0.3)',
                    }}
                  >
                    <Circle sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                      Traffic Sources
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#2D3748', fontWeight: 500 }}>
                      Customer acquisition channels
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ height: 200, display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <Doughnut data={trafficData} options={doughnutOptions} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'center' }}>
                      <Circle sx={{ fontSize: 12, color: '#4299E1' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Direct</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#4299E1' }}>45%</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'center' }}>
                      <Circle sx={{ fontSize: 12, color: '#48BB78' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Social</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#48BB78' }}>30%</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'center' }}>
                      <Circle sx={{ fontSize: 12, color: '#ED8936' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Referral</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#ED8936' }}>25%</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Enhanced Recent Activity */}
        <Grid item xs={12} lg={6}>
          <Fade in timeout={900} style={{ transitionDelay: '700ms' }}>
            <Card 
              sx={{ 
                height: 420, 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(232, 180, 184, 0.2)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #48BB78 0%, #4299E1 100%)',
                },
              }}
            >
              <CardContent sx={{ pb: 1, p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #48BB78 0%, #4299E1 100%)',
                        boxShadow: '0 4px 12px rgba(72, 187, 120, 0.3)',
                      }}
                    >
                      <Notifications sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        Recent Activity
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#2D3748', fontWeight: 500 }}>
                        Latest updates and notifications
                      </Typography>
                    </Box>
                  </Box>
                  <Chip 
                    label="Live" 
                    size="small" 
                    sx={{ 
                      bgcolor: '#48BB78', 
                      color: 'white',
                      fontWeight: 700,
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.7 },
                        '100%': { opacity: 1 },
                      }
                    }} 
                  />
                </Box>
              </CardContent>
              <Box sx={{ 
                flex: 1, 
                overflow: 'auto',
                px: 3,
                pb: 2,
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(232, 180, 184, 0.1)',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#E8B4B8',
                  borderRadius: '3px',
                  '&:hover': {
                    background: '#D4969B',
                  },
                },
              }}>
                <List sx={{ py: 0 }}>
                  <ActivityItem
                    type="order"
                    title="New appointment booked"
                    description="Bridal makeup appointment by Priya Sharma"
                    time="2 minutes ago"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ActivityItem
                    type="customer"
                    title="New customer registered"
                    description="Anita Gupta joined our platform"
                    time="15 minutes ago"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ActivityItem
                    type="order"
                    title="Payment received"
                    description="₹5,500 payment for Package #PKG-001"
                    time="32 minutes ago"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ActivityItem
                    type="alert"
                    title="Low stock alert"
                    description="Lakme Foundation is running low (5 units left)"
                    time="1 hour ago"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ActivityItem
                    type="update"
                    title="Service completed"
                    description="Hair styling service completed for Meera Patel"
                    time="2 hours ago"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ActivityItem
                    type="customer"
                    title="Review received"
                    description="5-star review from Kavya Singh"
                    time="3 hours ago"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ActivityItem
                    type="order"
                    title="Appointment rescheduled"
                    description="Facial appointment moved to tomorrow"
                    time="4 hours ago"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ActivityItem
                    type="alert"
                    title="Staff check-in"
                    description="Riya checked in for her shift"
                    time="5 hours ago"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ActivityItem
                    type="update"
                    title="Inventory updated"
                    description="New stock added for MAC products"
                    time="6 hours ago"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ActivityItem
                    type="customer"
                    title="Loyalty points redeemed"
                    description="Sneha Reddy redeemed 500 points"
                    time="8 hours ago"
                  />
                </List>
              </Box>
            </Card>
          </Fade>
        </Grid>

        {/* Enhanced Top Products */}
        <Grid item xs={12} lg={6}>
          <Fade in timeout={1000} style={{ transitionDelay: '800ms' }}>
            <Card 
              sx={{ 
                height: 420, 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(232, 180, 184, 0.2)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #E8B4B8 0%, #9F7AEA 100%)',
                },
              }}
            >
              <CardContent sx={{ pb: 1, p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #E8B4B8 0%, #9F7AEA 100%)',
                        boxShadow: '0 4px 12px rgba(232, 180, 184, 0.3)',
                      }}
                    >
                      <ShoppingCart sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        Top Products & Services
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#2D3748', fontWeight: 500 }}>
                        Best performing items this month
                      </Typography>
                    </Box>
                  </Box>
                  <Chip 
                    label="This Month" 
                    size="small" 
                    variant="outlined"
                    sx={{ 
                      borderColor: '#E8B4B8',
                      color: '#E8B4B8',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                    }} 
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, px: 2 }}>
                  <Typography variant="caption" sx={{ color: '#2D3748', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                    PRODUCT/SERVICE
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Typography variant="caption" sx={{ color: '#2D3748', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                      SALES
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#2D3748', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                      REVENUE
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#2D3748', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                      STATUS
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
              <Box sx={{ 
                flex: 1, 
                overflow: 'auto',
                px: 3,
                pb: 2,
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(232, 180, 184, 0.1)',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#E8B4B8',
                  borderRadius: '3px',
                  '&:hover': {
                    background: '#D4969B',
                  },
                },
              }}>
                <Box>
                  <ProductItem
                    name="Bridal Makeup Package"
                    sales={245}
                    revenue="₹12,25,000"
                    status="trending"
                    color="#E8B4B8"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ProductItem
                    name="Hair Styling & Cut"
                    sales={190}
                    revenue="₹9,50,000"
                    status="trending"
                    color="#9F7AEA"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ProductItem
                    name="Facial Treatment"
                    sales={156}
                    revenue="₹4,68,000"
                    status="stable"
                    color="#4299E1"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ProductItem
                    name="Manicure & Pedicure"
                    sales={98}
                    revenue="₹2,94,000"
                    status="trending"
                    color="#48BB78"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ProductItem
                    name="Lakme Foundation"
                    sales={87}
                    revenue="₹2,61,000"
                    status="declining"
                    color="#ED8936"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ProductItem
                    name="Threading & Waxing"
                    sales={76}
                    revenue="₹1,52,000"
                    status="stable"
                    color="#38B2AC"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ProductItem
                    name="MAC Lipstick Collection"
                    sales={65}
                    revenue="₹1,95,000"
                    status="trending"
                    color="#F56565"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ProductItem
                    name="Pre-Wedding Package"
                    sales={54}
                    revenue="₹8,10,000"
                    status="trending"
                    color="#9F7AEA"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ProductItem
                    name="Hair Spa Treatment"
                    sales={43}
                    revenue="₹1,29,000"
                    status="stable"
                    color="#4299E1"
                  />
                  <Divider sx={{ my: 1, borderColor: 'rgba(232, 180, 184, 0.1)' }} />
                  <ProductItem
                    name="Loreal Hair Products"
                    sales={32}
                    revenue="₹96,000"
                    status="declining"
                    color="#ED8936"
                  />
                </Box>
              </Box>
            </Card>
          </Fade>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;